import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, MapPin, Clock, Airplane, UsersThree, Suitcase, Baby, Phone, CheckCircle, XCircle, SteeringWheel, Camera } from "@phosphor-icons/react";

const STATUS_BADGE = {
  pending: { label: "Warten auf Bestätigung", cls: "bg-[#FEF3C7] text-[#92400E]" },
  accepted: { label: "Bestätigt", cls: "bg-[#DCFCE7] text-[#166534]" },
  declined: { label: "Abgelehnt", cls: "bg-[#FEE2E2] text-[#991B1B]" },
};

export default function DriverPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/driver/${token}`); setData(r.data); }
    catch { setErr(true); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/driver/${token}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto aktualisiert");
      await load();
    } catch { toast.error("Upload fehlgeschlagen"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const respond = async (ref, accept) => {
    setBusy(ref + accept);
    try {
      await api.post(`/driver/${token}/bookings/${ref}/respond`, { accept });
      toast.success(accept ? "Fahrt bestätigt" : "Fahrt abgelehnt");
      await load();
    } catch { toast.error("Fehler"); } finally { setBusy(""); }
  };

  if (err) return <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="driver-invalid"><XCircle size={44} weight="fill" className="text-[#EF4444] mx-auto" /><h1 className="font-heading text-2xl mt-4 text-[#0B1533]">Ungültiger oder abgelaufener Link</h1><p className="text-[#5A607A] mt-2 font-body">Bitte kontaktieren Sie den Disponenten für einen neuen Link.</p></div>;
  if (!data) return <div className="py-32 flex justify-center"><CircleNotch size={40} className="animate-spin text-[#EF5A1E]" /></div>;

  const trips = data.bookings || [];
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="driver-portal">
      <div className="flex items-center gap-4 mb-2">
        <div className="relative">
          {data.driver.photo ? (
            <img src={mediaUrl(data.driver.photo)} alt={data.driver.name} className="w-16 h-16 rounded-2xl object-cover border-2 border-[#EF5A1E]" data-testid="driver-photo" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-[#0B1533] flex items-center justify-center"><SteeringWheel size={28} weight="fill" className="text-[#EF5A1E]" /></div>
          )}
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="driver-photo-btn" className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-[#EF5A1E] text-white flex items-center justify-center shadow-md hover:bg-[#D2450F] transition-colors disabled:opacity-50">
            {uploading ? <CircleNotch size={16} className="animate-spin" /> : <Camera size={16} weight="fill" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="driver-photo-input" />
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-[0.16em] text-[#5A607A]">Fahrer-Portal</p>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533]" data-testid="driver-name">{data.driver.name}</h1>
        </div>
      </div>
      <p className="font-body text-sm text-[#5A607A] mb-8">{data.driver.vehicle} · {data.driver.plate} · <button type="button" onClick={() => fileRef.current?.click()} className="text-[#EF5A1E] hover:underline">{data.driver.photo ? "Foto ändern" : "Profilfoto hinzufügen"}</button></p>

      {trips.length === 0 && <div className="border border-[#E9E5DE] rounded-2xl p-10 text-center font-body text-[#5A607A]" data-testid="driver-no-trips">Aktuell keine zugewiesenen Fahrten.</div>}

      <div className="space-y-5">
        {trips.map((b) => {
          const badge = STATUS_BADGE[b.driver_status] || STATUS_BADGE.pending;
          return (
            <div key={b.reference} className="border border-[#E9E5DE] rounded-3xl p-6 bg-white" data-testid={`driver-trip-${b.reference}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <span className="font-heading font-semibold text-[#EF5A1E]">{b.reference}</span>
                <span className={`text-xs font-body font-semibold px-3 py-1 rounded-full ${badge.cls}`} data-testid={`driver-trip-status-${b.reference}`}>{badge.label}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-y-3 gap-x-6 mt-4 font-body text-sm text-[#0B1533]">
                <div className="flex items-start gap-2"><MapPin size={17} className="text-[#9CA0B0] mt-0.5 shrink-0" /><span>{b.pickup_name} → {b.dropoff_name}</span></div>
                <div className="flex items-center gap-2"><Clock size={17} className="text-[#9CA0B0]" /><span>{b.date} · {b.time}</span></div>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1.5"><UsersThree size={16} className="text-[#9CA0B0]" />{b.passengers}</span>
                  <span className="flex items-center gap-1.5"><Suitcase size={16} className="text-[#9CA0B0]" />{b.luggage}</span>
                  {b.children > 0 && <span className="flex items-center gap-1.5"><Baby size={16} className="text-[#9CA0B0]" />{b.children}</span>}
                </div>
                {b.flight_number && <div className="flex items-center gap-2"><Airplane size={16} className="text-[#9CA0B0]" /><span>{b.flight_number}</span></div>}
                <div className="flex items-center gap-2"><Phone size={16} className="text-[#9CA0B0]" /><span>{b.customer_name} · {b.customer_phone}</span></div>
                <div className="font-semibold">CHF {Number(b.total).toFixed(2)}</div>
              </div>
              {b.notes && <p className="mt-3 text-sm text-[#5A607A] font-body bg-[#F5F2ED] rounded-xl px-4 py-2">{b.notes}</p>}
              {b.driver_status === "pending" && (
                <div className="flex gap-3 mt-5">
                  <button data-testid={`driver-accept-${b.reference}`} disabled={busy === b.reference + true} onClick={() => respond(b.reference, true)} className="inline-flex items-center gap-2 bg-[#16A34A] text-white font-body font-semibold text-sm rounded-full px-6 py-3 hover:bg-[#15803D] transition-colors duration-200 disabled:opacity-50"><CheckCircle size={17} weight="fill" /> Annehmen</button>
                  <button data-testid={`driver-decline-${b.reference}`} disabled={busy === b.reference + false} onClick={() => respond(b.reference, false)} className="inline-flex items-center gap-2 border border-[#E9E5DE] text-[#0B1533] font-body font-semibold text-sm rounded-full px-6 py-3 hover:bg-[#FEE2E2] hover:border-[#FCA5A5] transition-colors duration-200 disabled:opacity-50"><XCircle size={17} weight="fill" className="text-[#EF4444]" /> Ablehnen</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
